import { FC, ReactNode } from 'react'

import styled, { useTheme } from 'styled-components'
import { Progress, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'

import { HUNDRED, DIVIDE_BY_TWO } from 'src/Constants/numeric'
import { PrimaryHint } from 'src/Components/PrimaryHint'
import { useSpotifyCampaignMatching } from 'src/Redux/spotifyCampaignMatchingApi'
import { useBudgetRange } from 'src/Redux/spotifyBudgetRangeApi'

const Blocks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`

const MetricBlock = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  min-height: 84px;
`

const CampaignReachBlock = styled(MetricBlock)`
  height: 141px;
`

const BlockHeader = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const BlockValue = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`

const CircularProgressBarWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledSpin = styled(Spin)`
  margin: auto auto;
`

const StyledPercentage = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  span {
    font-size: 12px;
    font-weight: 400;
  }
`

const STROKE_WIDTH = 7

const calculateReachPercentage = (
  minCuratorsCount: number | null,
  maxCuratorsCount: number | null,
  absoluteCuratorsCount: number,
): number => {
  if (!minCuratorsCount || !maxCuratorsCount) {
    return 0
  }
  const average = (minCuratorsCount + maxCuratorsCount) / DIVIDE_BY_TWO
  return Math.min((average / absoluteCuratorsCount) * HUNDRED, HUNDRED)
}

export const SpotifySettingBudgetEstimation: FC = () => {
  const theme = useTheme()

  const {
    maxCuratorsCount,
    minCuratorsCount,
    totalPlaylistFollowers,
    isFetching,
  } = useSpotifyCampaignMatching()
  const isLoading = isFetching || maxCuratorsCount === null
  const hasFollowers = Boolean(totalPlaylistFollowers)
  const { data } = useBudgetRange()
  const absoluteCuratorsCount = data?.absoluteCuratorsCount ?? 1

  const percentage = calculateReachPercentage(
    minCuratorsCount,
    maxCuratorsCount,
    absoluteCuratorsCount,
  )

  const customFormat = (): ReactNode => (
    <StyledPercentage>
      {Math.round(percentage)}
      <span>%</span>
    </StyledPercentage>
  )

  return (
    <Blocks>
      <MetricBlock>
        <BlockHeader>
          <div>Estimated curator submissions</div>
          <PrimaryHint>
            Estimated curator submissions are based on your selected budget,
            genres, and target languages.
          </PrimaryHint>
        </BlockHeader>
        {isLoading ? (
          <StyledSpin
            style={{ color: theme.color.brandGreen }}
            indicator={<LoadingOutlined spin />}
            size='large'
          />
        ) : (
          <BlockValue>{`Up to ${maxCuratorsCount ?? 0}`}</BlockValue>
        )}
      </MetricBlock>

      {(isLoading || hasFollowers) && (
        <MetricBlock>
          <BlockHeader>
            <div>Total playlist followers</div>
            <PrimaryHint>
              Total followers from matched curator playlists
            </PrimaryHint>
          </BlockHeader>
          {isLoading ? (
            <StyledSpin
              style={{ color: theme.color.brandGreen }}
              indicator={<LoadingOutlined spin />}
              size='large'
            />
          ) : (
            <BlockValue>
              {(totalPlaylistFollowers ?? 0).toLocaleString('en-US')}
            </BlockValue>
          )}
        </MetricBlock>
      )}

      <CampaignReachBlock>
        <BlockHeader>
          <div>Campaign reach</div>
          <PrimaryHint>
            Share of curators matched with your track which you can reach with
            selected budget
          </PrimaryHint>
        </BlockHeader>
        {isLoading ? (
          <StyledSpin
            style={{ color: theme.color.brandGreen }}
            indicator={<LoadingOutlined spin />}
            size='large'
          />
        ) : (
          <CircularProgressBarWrapper>
            <Progress
              type='circle'
              size={80}
              percent={percentage}
              strokeWidth={STROKE_WIDTH}
              trailColor={theme.color.lightGrey}
              strokeColor={theme.color.brandGreen}
              format={customFormat}
            />
          </CircularProgressBarWrapper>
        )}
      </CampaignReachBlock>
    </Blocks>
  )
}
